import {
  FC,
  KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import cn from 'classnames'

import { OkIcon } from '@innoscripta/icons'

import { PlaybackSpeedControlProps } from './PlaybackSpeedControl.types'
import { PLAYBACK_RATES } from './VideoPlayer.constants'
import styles from './VideoPlayer.module.scss'

export const PlaybackSpeedControl: FC<PlaybackSpeedControlProps> = ({
  isOpen,
  label,
  playbackRate,
  setIsOpen,
  onChange
}) => {
  const controlRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const menuId = useId()
  const [menuMaxHeight, setMenuMaxHeight] = useState(320)

  useLayoutEffect(() => {
    if (!isOpen) return

    const player = controlRef.current?.closest<HTMLElement>(
      '[data-video-player]'
    )
    if (!player) return

    const updateMenuMaxHeight = () => {
      const playerTop = player.getBoundingClientRect().top
      const controlTop = controlRef.current?.getBoundingClientRect().top
      if (controlTop === undefined) return

      const availableHeight = controlTop - playerTop - 8
      setMenuMaxHeight(
        Math.max(0, Math.min(320, window.innerHeight - 96, availableHeight))
      )
    }

    updateMenuMaxHeight()
    const resizeObserver = new ResizeObserver(updateMenuMaxHeight)
    resizeObserver.observe(player)
    window.addEventListener('resize', updateMenuMaxHeight)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateMenuMaxHeight)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const selectedIndex = PLAYBACK_RATES.indexOf(playbackRate)
    optionRefs.current[Math.max(0, selectedIndex)]?.focus()

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !controlRef.current?.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen, playbackRate, setIsOpen])

  const closeAndFocusTrigger = () => {
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = optionRefs.current.findIndex(
      option => option === document.activeElement
    )
    let nextIndex: number | undefined

    if (event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length
    } else if (event.key === 'ArrowUp') {
      nextIndex =
        (currentIndex - 1 + PLAYBACK_RATES.length) % PLAYBACK_RATES.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = PLAYBACK_RATES.length - 1
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeAndFocusTrigger()
      return
    } else {
      return
    }

    event.preventDefault()
    optionRefs.current[nextIndex]?.focus()
  }

  const selectRate = (rate: number) => {
    onChange(rate)
    closeAndFocusTrigger()
  }

  return (
    <div ref={controlRef} className={styles.speedControl}>
      <button
        ref={triggerRef}
        type='button'
        className={cn(styles.speedTrigger, {
          [styles.speedTriggerOpen]: isOpen
        })}
        aria-label={`${label}: ${playbackRate}×`}
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => setIsOpen(open => !open)}
        onKeyDown={event => {
          if (
            !isOpen &&
            (event.key === 'ArrowDown' || event.key === 'ArrowUp')
          ) {
            event.preventDefault()
            setIsOpen(true)
          }
        }}
      >
        {playbackRate}×
      </button>

      {isOpen && (
        <div
          id={menuId}
          className={styles.speedMenu}
          style={{ maxHeight: menuMaxHeight }}
          role='menu'
          aria-label={label}
          onKeyDown={handleMenuKeyDown}
        >
          <div className={styles.speedMenuTitle} aria-hidden='true'>
            {label}
          </div>
          {PLAYBACK_RATES.map((rate, index) => {
            const isSelected = rate === playbackRate

            return (
              <button
                key={rate}
                ref={element => {
                  optionRefs.current[index] = element
                }}
                type='button'
                role='menuitemradio'
                aria-checked={isSelected}
                className={cn(styles.speedOption, {
                  [styles.speedOptionSelected]: isSelected
                })}
                onClick={() => selectRate(rate)}
              >
                <span>{rate}×</span>
                {isSelected && (
                  <span className={styles.speedOptionCheck} aria-hidden='true'>
                    <OkIcon size={14} fill='currentColor' />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
